import classNames from 'classnames';
import Checkbox from '../../../../../../../../components/Checkbox';
import WarningIcon from '../../../../../../../../assets/icons/warning-grey-outlined.svg?react';

import {
  useGetTransform,
  useGetTransforms
} from '../../../../../../../../services/workflow.services';
import React, { useEffect, useMemo, useState } from 'react';
import { JoinConditions, JoinResultColumns, JoinTypes } from '../style';
import { useLookupDictionaries } from '../../../../../LookupTables/hooks/useLookupDictionaries';
import extractionStore from '../../../../../../store';
import {
  FullJoinSvg,
  FullJoinSvgActive,
  InnerJoinSvg,
  InnerJoinSvgActive,
  LeftJoinSvg,
  LeftJoinSvgActive
} from './icons';
import { ConditionCombination } from './ConditionCombination';

import { Flex, Radio, rem, Text } from '@mantine/core';
import { BiInfoCircle } from 'react-icons/bi';

export const Join = ({
  tableName,
  extractId,
  selectedTransformation,
  tranformationId,
  onChangeOperator,

  item,
  filterTypes,
  onDeleteSelectedItems,
  onToggleColumnItem2,

  addCondition,
  onChangeReplaceColumn,
  onChangeInputType,
  onChangeColumn,
  onToggleColumnItem,
  onChangeFuzzyMatching,
  onClearColumns,
  onClearColumns2,
  periodId
}) => {
  const filterType = filterTypes?.[0];
  const selectedType = filterType?.inputType;
  const isShowOtherSections = !!item?.replaceColumn && !!(item?.then?.column || item?.then?.input);

  const [firstColumns, setFirstColumns] = useState();
  const [secondColumns, setSecondColumns] = useState();
  const [requiredFirstColumns, setRequiredFirstColumns] = useState([]);
  const [requiredSecondColumns, setRequiredSecondColumns] = useState([]);

  // Compute warning state directly from filterType
  const hasJoinWarning = useMemo(() => !!filterType?.warning, [filterType?.warning]);

  useGetTransform({
    transformId: tranformationId,
    params: {
      step_in: selectedTransformation
    },
    queryParams: {
      enabled: !!tranformationId && selectedTransformation >= 0,
      onSuccess: res => {
        setFirstColumns(Object.values(res?.data?.[0] || {}).filter(el => el));
      }
    }
  });

  useGetTransforms({
    extractId: extractId,
    params: {
      extract_id: extractId
    },
    queryParams: {
      enabled: !!extractId && selectedTransformation !== 0 && !selectedTransformation,
      onSuccess: res => {
        if (selectedTransformation === 0) return;

        const foundTransform = res?.find(transf => transf?.table_name === item?.replaceColumn);

        setFirstColumns(Object.values(foundTransform?.data?.[0] || {}).filter(el => el));
      }
    }
  });

  useGetTransforms({
    extractId: filterType?.elseValue?.input,
    params: {
      extract_id: filterType?.elseValue?.input
    },
    queryParams: {
      enabled: !!filterType?.elseValue?.input,
      onSuccess: res => {
        const foundTransform = res?.find(
          transf => transf?.table_name === (item?.then?.column || item?.then?.input)
        );

        setSecondColumns(Object.values(foundTransform?.data?.[0] || {}).filter(el => el));
      }
    }
  });

  // When the second table is a lookup (in-memory sentinel:
  // elseValue.input === 0, normalizer remaps to `extraction_id: null` on the
  // wire), source the column list from the lookup dictionary instead of from
  // transforms — lookups can have `data: []` so we rely on `columns`.
  const isLookupSecondTable = filterType?.elseValue?.input === 0;
  const reportType = extractionStore.reportType;
  const lookupDicts = useLookupDictionaries({
    periodId: isLookupSecondTable ? periodId : undefined,
    reportType: isLookupSecondTable ? reportType : undefined
  });

  useEffect(() => {
    if (!isLookupSecondTable) return;
    const name = item?.then?.column || item?.then?.input;
    if (!name) return;
    const match = lookupDicts.all?.find(d => d.name === name);
    if (match) setSecondColumns(match.columns.map(c => c.key));
  }, [isLookupSecondTable, item?.then?.column, item?.then?.input, lookupDicts.all]);

  const removedColumnsFromFirstTable = useMemo(() => {
    if (!firstColumns || !filterType.columns) return [];
    return filterType?.columns?.filter(el => !(firstColumns || [])?.includes(el));
  }, [firstColumns, filterType.columns]);

  const removedColumnsFromSecondTable = useMemo(() => {
    if (!secondColumns || !filterType.columns2) return [];
    return filterType?.columns2?.filter(el => !(secondColumns || [])?.includes(el));
  }, [secondColumns, filterType.columns2]);

  useEffect(() => {
    if (!item?.replaceColumn) {
      onChangeReplaceColumn(filterType?.id, item?.id, tableName);
    }
  }, [item?.replaceColumn]);

  const onToggleAllColumns = () => {
    if (firstColumns?.length === filterType?.columns?.length) {
      onClearColumns(filterType?.id, filterType?.id, requiredFirstColumns);
    } else {
      firstColumns?.forEach(el => onToggleColumnItem(filterType?.id, filterType?.id, el, 'add'));
    }
  };

  const onToggleAllColumns2 = () => {
    if (secondColumns?.length === filterType?.columns2?.length) {
      onClearColumns2(filterType?.id, filterType?.id, requiredSecondColumns);
    } else {
      secondColumns?.forEach(el => onToggleColumnItem2(filterType?.id, filterType?.id, el, 'add'));
    }
  };

  const onChangeFirstTablesList = el => {
    onToggleColumnItem(filterType?.id, filterType?.id, el);
  };

  const onChangeSecondTablesList = el => {
    onToggleColumnItem2(filterType?.id, filterType?.id, el);
  };

  const onChangeTableColumn = (val, conditionId, prevVal) => {
    onChangeColumn(filterType?.id, conditionId, val);
  };

  const onChangeTableOperator = (val, conditionId, prevVal) => {
    onChangeOperator(filterType?.id, conditionId, val);
  };

  const isAllColumnsSelected = useMemo(
    () => firstColumns?.length === filterType?.columns?.length,
    [firstColumns, filterType?.columns]
  );

  const isAllColumns2Selected = useMemo(
    () => secondColumns?.length === filterType?.columns2?.length,
    [secondColumns, filterType?.columns2]
  );

  const hasSameSelectedItemColumn = useMemo(() => {
    return filterType?.columns?.filter(type => filterType?.columns2.includes(type))?.length > 0;
  }, [filterType?.columns, filterType?.columns2]);

  return (
    <Flex id="grid-table-join-root" direction="column" gap={rem(16)}>
      {hasJoinWarning && (
        <Flex
          id="grid-table-join-warning"
          align="center"
          gap={rem(16)}
          p="12px 12px 12px 16px"
          bg="orange.0"
          style={{ borderRadius: '4px' }}
        >
          <BiInfoCircle color="#FD7E14" size={20} />
          <Text size={rem(14)} c="orange.6">
            {filterType.warning}
          </Text>
        </Flex>
      )}
      {isShowOtherSections && (
        <>
          <JoinTypes id="grid-table-join-types">
            <Text id="grid-table-join-types-title" size={rem(14)} fw={600}>
              Join Type {item.errors?.includes('then') && ' - Required'}
            </Text>

            <div id="grid-table-join-type-buttons" className="types">
              <button
                id="grid-table-join-type-left"
                className={classNames('type', {
                  active: selectedType === 'left'
                })}
                onClick={() => onChangeInputType(filterType?.id, filterType?.id, 'left')}
              >
                <span className="icon">
                  {selectedType === 'left' ? <LeftJoinSvgActive /> : <LeftJoinSvg />}
                </span>

                <Radio
                  id="grid-table-join-type-left-radio"
                  className="toggle"
                  checked={selectedType === 'left'}
                  size="sm"
                />

                <span className="text">All from first, matching from second</span>
              </button>
              <button
                id="grid-table-join-type-inner"
                className={classNames('type', {
                  active: selectedType === 'inner'
                })}
                onClick={() => onChangeInputType(filterType?.id, filterType?.id, 'inner')}
              >
                <span className="icon">
                  {selectedType === 'inner' ? <InnerJoinSvgActive /> : <InnerJoinSvg />}
                </span>

                <Radio
                  id="grid-table-join-type-inner-radio"
                  className="toggle"
                  checked={selectedType === 'inner'}
                  size="sm"
                />

                <span className="text">
                  Only matching
                  <br /> rows
                </span>
              </button>
              <button
                id="grid-table-join-type-outer"
                className={classNames('type', {
                  active: selectedType === 'outer'
                })}
                onClick={() => onChangeInputType(filterType?.id, filterType?.id, 'outer')}
              >
                <span className="icon">
                  {selectedType === 'outer' ? <FullJoinSvgActive /> : <FullJoinSvg />}
                </span>

                <Radio
                  id="grid-table-join-type-outer-radio"
                  className="toggle"
                  checked={selectedType === 'outer'}
                  size="sm"
                />

                <span className="text">
                  All rows from
                  <br /> both
                </span>
              </button>
            </div>
          </JoinTypes>

          <JoinConditions id="grid-table-join-conditions">
            <Text id="grid-table-join-conditions-title" size={rem(14)} fw={600}>
              Join Conditions
            </Text>

            <div id="grid-table-join-conditions-list" className="conditions">
              {item.conditions?.map((condition, idx) => (
                <ConditionCombination
                  key={idx}
                  column={condition.column}
                  columns={firstColumns}
                  secondColumns={secondColumns}
                  operator={condition.operator}
                  fuzzyMatching={condition.fuzzyMatching}
                  disableDelete={item.conditions?.length === 1}
                  onChangeFuzzyMatching={val =>
                    onChangeFuzzyMatching(filterType?.id, condition?.id, val)
                  }
                  onChangeColumn={val => onChangeTableColumn(val, condition?.id, condition.column)}
                  onChangeOperator={val =>
                    onChangeTableOperator(val, condition?.id, condition.operator)
                  }
                  onAddCondition={() => addCondition(filterType?.id, null, filterType?.id)}
                  onDeleteCondition={() => onDeleteSelectedItems(filterType?.id, [condition?.id])}
                />
              ))}
            </div>
          </JoinConditions>

          <JoinResultColumns id="grid-table-join-result-columns">
            <Text id="grid-table-join-result-columns-title" size={rem(14)} fw={600}>
              Select result columns
            </Text>

            {hasSameSelectedItemColumn && (
              <Flex
                id="grid-table-join-warning-selected-columns"
                c="red.6"
                my={rem(8)}
                gap={rem(4)}
                align="center"
              >
                <BiInfoCircle color="red" size={12} />
                <Text size={rem(12)} fw={500}>
                  You have same columns selected from both tables, the same column from second table
                  will be joined with suffix.
                </Text>
              </Flex>
            )}

            <div id="grid-table-join-result-box" className="box">
              <div id="grid-table-join-first-columns" className="columns">
                <Text id="grid-table-join-first-columns-title" size={rem(14)} fw={400}>
                  First Table
                </Text>

                <div id="grid-table-join-first-select-all" className="checkbox">
                  <button
                    id="grid-table-join-first-select-all-button"
                    onClick={onToggleAllColumns}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: 0
                    }}
                  >
                    <Checkbox
                      id="grid-table-join-first-select-all-checkbox"
                      onChange={onToggleAllColumns}
                      name="columns"
                      checked={isAllColumnsSelected}
                    />

                    <Text size={rem(14)} c="#101113" fw={400} ta="left">
                      {!isAllColumnsSelected ? 'Select All' : 'Unselect All'}
                    </Text>
                  </button>
                </div>

                {removedColumnsFromFirstTable?.map((el, idx) => (
                  <div className="checkbox" key={idx}>
                    <button
                      id={`grid-table-join-first-removed-${idx}`}
                      onClick={e => onChangeFirstTablesList(el)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: 0
                      }}
                    >
                      <Checkbox
                        id={`grid-table-join-first-removed-checkbox-${idx}`}
                        value={el}
                        name="columns"
                        checked={filterType?.columns?.includes(el)}
                        disabled={requiredFirstColumns.includes(el)}
                        onChange={e => onChangeFirstTablesList(el)}
                      />

                      <Text size={rem(14)} c="red" fw={400} ta="left">
                        {el}
                      </Text>

                      <WarningIcon
                        data-tooltip-content="This column might be deleted in previous steps"
                        data-tooltip-id="ligth-tooltip"
                      />
                    </button>
                  </div>
                ))}

                {firstColumns?.map((el, idx) => (
                  <div className="checkbox" key={idx}>
                    <button
                      id={`grid-table-join-first-${idx}`}
                      onClick={e => onChangeFirstTablesList(el)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: 0
                      }}
                    >
                      <Checkbox
                        id={`grid-table-join-first-checkbox-${idx}`}
                        value={el}
                        name="columns"
                        checked={filterType?.columns?.includes(el)}
                        disabled={requiredFirstColumns.includes(el)}
                        onChange={e => onChangeFirstTablesList(el)}
                      />

                      <Text size={rem(14)} c="#101113" fw={400} ta="left">
                        {el}
                      </Text>

                      {requiredFirstColumns.includes(el) && <span style={{ color: 'red' }}>*</span>}
                    </button>
                  </div>
                ))}
              </div>

              <div id="grid-table-join-second-columns" className="columns">
                <Text id="grid-table-join-second-columns-title" size={rem(14)} fw={400}>
                  Second Table
                </Text>

                <div id="grid-table-join-second-select-all" className="checkbox">
                  <button
                    id="grid-table-join-second-select-all-button"
                    onClick={onToggleAllColumns2}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: 0
                    }}
                  >
                    <Checkbox
                      id="grid-table-join-second-select-all-checkbox"
                      onChange={onToggleAllColumns2}
                      name="columns"
                      checked={isAllColumns2Selected}
                    />

                    <Text size={rem(14)} c="#101113" fw={400} ta="left">
                      {!isAllColumns2Selected ? 'Select All' : 'Unselect All'}
                    </Text>
                  </button>
                </div>

                {removedColumnsFromSecondTable?.map((el, idx) => (
                  <div className="checkbox" key={idx}>
                    <button
                      id={`grid-table-join-second-removed-${idx}`}
                      onClick={_ => onChangeSecondTablesList(el)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: 0
                      }}
                    >
                      <Checkbox
                        id={`grid-table-join-second-removed-checkbox-${idx}`}
                        value={el}
                        name="columns"
                        checked={filterType?.columns2?.includes(el)}
                        disabled={requiredSecondColumns.includes(el)}
                        onChange={e => onChangeSecondTablesList(el)}
                      />

                      <Text size={rem(14)} c="red" fw={400} ta="left">
                        {el}
                      </Text>

                      <WarningIcon
                        data-tooltip-content="This column might be deleted in previous steps"
                        data-tooltip-id="ligth-tooltip"
                      />
                    </button>
                  </div>
                ))}

                {secondColumns?.map((el, idx) => (
                  <div className="checkbox" key={idx}>
                    <button
                      id={`grid-table-join-second-${idx}`}
                      onClick={_ => onChangeSecondTablesList(el)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: 0
                      }}
                    >
                      <Checkbox
                        id={`grid-table-join-second-checkbox-${idx}`}
                        value={el}
                        name="columns"
                        checked={filterType?.columns2?.includes(el)}
                        disabled={requiredSecondColumns.includes(el)}
                        onChange={_ => onChangeSecondTablesList(el)}
                      />

                      <Text size={rem(14)} c="#101113" fw={400}>
                        {el}
                      </Text>

                      {requiredSecondColumns.includes(el) && (
                        <span style={{ color: 'red' }}>*</span>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </JoinResultColumns>
        </>
      )}
    </Flex>
  );
};
